import { invert, memoize } from 'lodash'
import Vue from 'vue'
import Vuex from 'vuex'

import * as CONST from '../const/shared'
import { formatHourToFloat } from '@/services/time'

Vue.use(Vuex)

const initialState = () => ({
  acceptBooking: true,
  appointmentAlert: null,
  currentShopUuid: '',
  modal: {
    confirmModal: {
      display: false
    }
  },
  myUser: {},
  notificationsUpToDate: false,
  shops: [],
  calendarSelectedAppointment: null,
  skipSetMyUser: false,
  pendingLogo: null
})

const _memoShop = memoize(shop => {
  if (Array.isArray(shop.openings)) {
    const dayNums = invert(CONST.dayOfWeekByKey)
    shop.openings.sort((a, b) => {
      if (a.dayOfWeek === b.dayOfWeek) {
        return a.beginHour.localeCompare(b.beginHour)
      }

      return dayNums[a.dayOfWeek] - dayNums[b.dayOfWeek]
    })

    // check for service opening overlapps
    for (const service of shop.services.filter(s => s.enabled)) {
      const toDelete = []

      for (let i = 0; i < service.openings.length - 1; i++) {
        const current = service.openings[i]
        const next = service.openings[i + 1]

        if (current.dayOfWeek !== next.dayOfWeek) {
          continue
        }

        const currentStart = formatHourToFloat(current.beginHour)
        const currentStop = formatHourToFloat(current.endHour)
        const nextStart = formatHourToFloat(next.beginHour)
        const nextStop = formatHourToFloat(next.endHour)

        if (nextStart <= currentStop && nextStop >= currentStart) {
          next.beginHour = nextStart < currentStart ? next.beginHour : current.beginHour
          next.endHour = nextStop < currentStart ? current.endHour : next.endHour

          toDelete.push(i)
        }
      }

      for (const index of toDelete.reverse()) {
        service.openings.splice(index, 1)
      }
    }
  }

  return shop
})

export default new Vuex.Store({
  state: initialState(),
  getters: {
    currentShop: state => {
      const shop = state.shops.find(shop => shop.shopUuid === state.currentShopUuid)
      if (!shop) {
        return
      }

      return _memoShop(shop)
    }
  },
  mutations: {
    SET_PENDING_LOGO(state, payload) {
      state.pendingLogo = payload
    },
    CLOSE_CONFIRM_MODAL(state) {
      state.modal.confirmModal = { display: false }
    },
    RESET_APPOINTMENT_ALERT(state) {
      state.appointmentAlert = null
    },
    RESET_CALENDAR_SELECTED_APPOINTMENT(state) {
      state.calendarSelectedAppointment = null
    },
    RESET_STORE(state) {
      const init = initialState()
      Object.keys(init).forEach(key => {
        state[key] = init[key]
      })
    },
    SET_ACCEPT_BOOKING(state, payload) {
      state.acceptBooking = payload
    },
    SET_CALENDAR_SELECTED_APPOINTMENT(state, payload) {
      state.calendarSelectedAppointment = payload
    },
    SET_CONFIRM_MODAL(state, payload) {
      state.modal.confirmModal = payload
    },
    SET_CREATED_APPOINTMENT_ALERT(state, payload) {
      state.appointmentAlert = payload
    },
    SET_MY_USER(state, payload) {
      if (!payload) return
      const { shops, ...user } = payload
      state.myUser = user

      state.shops = shops || []
      if (shops[0]) {
        state.currentShopUuid = shops[0].shopUuid
      }
    },
    SET_NOTIFICATIONS_UP_TO_DATE(state, payload) {
      state.modal.notificationsUpToDate = payload
    },
    SET_SHOP(state, payload) {
      state.shops = state.shops.map(shop => {
        if (shop.shopUuid === state.currentShopUuid) {
          return {
            ...shop,
            ...payload,
            openings: [...payload.openings],
            maxParallelAppointments: { ...payload.maxParallelAppointments }
          }
        } else {
          return shop
        }
      })
    },
    INIT_SCHEDULE(state, payload) {
      const currentShop = state.shops.find(shop => shop.shopUuid === payload.shopUuid)
      currentShop.openings = payload.data
      currentShop.maxParallelAppointments = payload.maxParallelAppointments
    },
    SELECT_SHOP_SERVICES(state, { selectShopServices: payload }) {
      const currentShop = state.shops.find(shop => shop.shopUuid === state.currentShopUuid)
      currentShop.services = payload.services
    },
    SETUP_SHOP_SERVICE(state, { service }) {
      const currentShop = state.shops.find(shop => shop.shopUuid === state.currentShopUuid)
      const serviceIndex = currentShop.services.findIndex(
        serviceStore => serviceStore.serviceUuid === service.serviceUuid
      )
      if (serviceIndex >= 0) {
        currentShop.services[serviceIndex] = service
      } else {
        currentShop.services.push(service)
      }
    },
    SETUP_SHOP_SERVICES(state, { setupShopServices }) {
      const currentShop = state.shops.find(shop => shop.shopUuid === state.currentShopUuid)
      currentShop.isOnboarded = setupShopServices.isOnboarded
      currentShop.services = setupShopServices.services
    },
    UPDATE_NOTIFICATIONS_UPDATE_DATE(state) {
      const currentShop = state.shops.find(shop => shop.shopUuid === state.currentShopUuid)
      currentShop.notificationACKUpdatedAt = new Date().toISOString()
    },
    SET_SKIP_MY_USER(state, payload) {
      state.skipSetMyUser = payload
    }
  },
  actions: {},
  modules: {}
})
